import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';

@Component({
  selector: 'app-admin-courses',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, NavbarComponent],
  templateUrl: './admin-courses.component.html',
  styleUrl: './admin-users.component.css'
})
export class AdminCoursesComponent implements OnInit {
  @Input() embedded = false;
  sessionId = '';
  content: any[] = [];
  seeds: { id: number; name: string }[] = [];
  selectedSeedId: number | null = null;
  loading = false;
  error = '';
  message = '';
  detailItem: any = null;
  detailForm = { name: '', seedId: null as number | null, active: 1 };
  detailSaving = false;
  deleteItem: any = null;
  deleteError = '';
  sortColumn = 'name';
  sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private adminRest: AdminRestService
  ) {}

  async ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    await this.loadSeeds();
    const defaultSeed = await this.adminRest.getAdminSeedDefault(this.sessionId);
    if (defaultSeed?.id != null) {
      this.selectedSeedId = defaultSeed.id;
    } else if (this.seeds.length > 0) {
      this.selectedSeedId = this.seeds[0].id;
    }
    await this.load();
  }

  async load() {
    if (!this.sessionId) return;
    this.loading = true;
    this.error = '';
    try {
      const list = await this.adminRest.getAdminCourses(this.sessionId, undefined, this.selectedSeedId ?? undefined);
      this.content = list ?? [];
    } catch (e: any) {
      this.error = e?.message || 'Failed to load courses';
    } finally {
      this.loading = false;
    }
  }

  async loadSeeds() {
    if (!this.sessionId) return;
    const list = await this.adminRest.getAdminSeeds(this.sessionId);
    this.seeds = list ?? [];
  }

  onSeedChange() {
    this.load();
  }

  openCreate() {
    this.detailItem = { id: null };
    this.detailForm = { name: '', seedId: this.selectedSeedId, active: 1 };
  }

  openEdit(c: any) {
    this.detailItem = c;
    this.detailForm = { name: c.name ?? '', seedId: c.seedId ?? null, active: c.active ?? 1 };
  }

  closeDetail() {
    this.detailItem = null;
    this.load();
  }

  async save() {
    if (!this.sessionId) return;
    this.detailSaving = true;
    this.error = '';
    try {
      if (this.detailItem?.id == null) {
        if (!this.detailForm.name?.trim()) {
          this.error = 'Name is required';
          this.detailSaving = false;
          return;
        }
        if (!this.detailForm.seedId) {
          this.error = 'Seed is required';
          this.detailSaving = false;
          return;
        }
        await this.adminRest.postAdminCourse(this.sessionId, {
          name: this.detailForm.name.trim(),
          seedId: this.detailForm.seedId,
          active: this.detailForm.active ?? 1
        });
        this.message = 'Course created.';
      } else {
        await this.adminRest.putAdminCourse(this.sessionId, this.detailItem.id, {
          name: this.detailForm.name,
          seedId: this.detailForm.seedId,
          active: this.detailForm.active
        });
        this.message = 'Course updated.';
      }
      this.closeDetail();
    } catch (e: any) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.detailSaving = false;
    }
  }

  openDeleteConfirm(c: any) {
    this.deleteItem = c;
    this.deleteError = '';
  }

  closeDeleteConfirm() {
    this.deleteItem = null;
    this.deleteError = '';
  }

  seedName(seedId: number | null | undefined): string {
    if (seedId == null) return '—';
    const s = this.seeds.find(se => se.id === seedId);
    return s?.name ?? String(seedId);
  }

  get sortedContent(): any[] {
    const col = this.sortColumn;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...this.content].sort((a, b) => {
      let va: any = a[col];
      let vb: any = b[col];
      if (col === 'name' || col === 'createdAt') {
        va = (va ?? '') + '';
        vb = (vb ?? '') + '';
        return dir * (va.localeCompare(vb, undefined, { sensitivity: 'base' }));
      }
      if (col === 'active') {
        va = a.active ? 1 : 0;
        vb = b.active ? 1 : 0;
      }
      if (va == null) va = '';
      if (vb == null) vb = '';
      return dir * (va < vb ? -1 : va > vb ? 1 : 0);
    });
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDir = 'asc';
    }
  }

  sortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }

  async confirmDelete() {
    if (!this.deleteItem?.id || !this.sessionId) return;
    this.deleteError = '';
    try {
      await this.adminRest.deleteAdminCourse(this.sessionId, this.deleteItem.id);
      this.closeDeleteConfirm();
      this.load();
    } catch (e: any) {
      this.deleteError = e?.error?.error || e?.message || 'Delete failed';
    }
  }
}
